import React, { useState, useEffect, useMemo } from "react";
import { API_URL } from "../../services/api.js";
import { IoIosArrowDown, IoMdClose } from "react-icons/io";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import toast from "react-hot-toast";
import { FiRefreshCw } from "react-icons/fi";
import { DataState } from "../../context/DataProvider.js";

const GohighlevelResourceModal = ({ onClose }) => {
    const { userGoogleId } = DataState();

    const [accountInfo, setAccountInfo] = useState(null);
    const [subaccountsList, setSubaccountsList] = useState([]);
    const [selectedSubResourceKeys, setSelectedSubResourceKeys] = useState(new Set());

    const [searchTerm, setSearchTerm] = useState("");

    const [loading, setLoading] = useState(true);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(null);

    const [expandedId, setExpandedId] = useState(null);

    const [showCompanyId, setShowCompanyId] = useState(false);
    const [showLocationId, setShowLocationId] = useState(false);
    const [showUserId, setShowUserId] = useState(false);

    const processFetchedData = (data) => {
        const account = data.account_info;

        setAccountInfo({
            userType: account.userType,
            companyId: account.companyId,
            locationId: account.locationId || null,
            userId: account.userId,
            approveAllLocations: account.approveAllLocations,
            restrictSubAccount: account.restrictSubAccount,
        });

        const subaccounts = account.subaccounts || [];
        const selectedIds = new Set(account.selectedSubaccounts || []);

        setSubaccountsList(subaccounts);
        setSelectedSubResourceKeys(selectedIds);
        setLastRefreshed(data.timestamp || null);
    };

    const fetchResources = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/gohighlevel/resources`, {
                method: "GET",
                headers: { Authorization: `Bearer ${userGoogleId}` },
                credentials: "include"
            });
            if (!res.ok) throw new Error("Fetch failed");

            const json = await res.json();
            if (!json.success) throw new Error(json.message || "Fetch failed");

            processFetchedData(json.data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResources();
    }, []);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        toast.loading("Refreshing...");

        try {
            const res = await fetch(`${API_URL}/gohighlevel/resources/refresh`, {
                method: "GET",
                headers: { Authorization: `Bearer ${userGoogleId}` },
                credentials: "include"
            });
            if (!res.ok) throw new Error("Refresh failed");

            const json = await res.json();
            if (!json.success) throw new Error(json.message || "Refresh failed");

            processFetchedData(json.data);

            toast.dismiss();
            toast.success("Resources refreshed");
        } catch (err) {
            toast.dismiss();
            toast.error(err.message);
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleToggleSubResource = (id) => {
        setSelectedSubResourceKeys(prev => {
            const updated = new Set(prev);

            const willBeSelected = !updated.has(id);

            if (willBeSelected) {
                updated.add(id);
                setExpandedId(id);
            } else {
                updated.delete(id);
                if (expandedId === id) setExpandedId(null);
            }

            return updated;
        });
    };

    const filteredSubaccounts = useMemo(() => {
        return subaccountsList.filter(sub =>
            sub.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [subaccountsList, searchTerm]);

    const handleSelectAll = () => {
        const allIds = filteredSubaccounts.map(sub => sub.id);
        setSelectedSubResourceKeys(new Set(allIds));
    };

    const handleDeselectAll = () => {
        setSelectedSubResourceKeys(new Set());
    };

    const handleConfirmSelection = async () => {
        setConfirmLoading(true);
        try {
            const payload = {
                selectedSubaccounts: Array.from(selectedSubResourceKeys),
            };

            const res = await fetch(`${API_URL}/gohighlevel/resources/select`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${userGoogleId}`
                },
                body: JSON.stringify(payload),
                credentials: "include"
            });

            if (!res.ok) throw new Error("Failed to save selection");

            toast.success("Configuration saved!");
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setConfirmLoading(false);
        }
    };

    const RevealField = ({ label, value, show, toggle }) => (
        <p className="flex items-center gap-3">
            <span className="font-medium">{label}:</span>

            {show ? (
                <button
                    onClick={toggle}
                    className="flex items-center gap-2 bg-black bg-opacity-60 px-3 py-1 rounded-full text-xs text-zinc-400 hover:bg-zinc-900 transition"
                >
                    {value}
                    <IoMdEyeOff />
                </button>
            ) : (
                <button
                    onClick={toggle}
                    className="flex items-center gap-2 bg-black bg-opacity-60 px-3 py-1 rounded-full text-xs text-zinc-400 hover:bg-zinc-900 transition"
                >
                    Click to reveal
                    <IoMdEye />
                </button>
            )}
        </p>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-2 border border-zinc-700 w-full max-w-xl max-h-[90%] rounded-lg p-6 flex flex-col">

                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-zinc-200">GoHighLevel Configuration</h2>
                        {lastRefreshed && (
                            <p className="text-xs text-zinc-400">
                                Last updated: {new Date(lastRefreshed).toLocaleString()}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center">
                        <button onClick={handleRefresh} disabled={isRefreshing}
                            className="p-2 mr-2 text-zinc-400 hover:text-white disabled:opacity-50">
                            <FiRefreshCw className={isRefreshing ? "animate-spin" : ""} />
                        </button>

                        <button onClick={onClose} className="text-zinc-500 hover:text-white">
                            <IoMdClose size={22} />
                        </button>
                    </div>
                </div>

                {/* Account Info */}
                {accountInfo && (
                    <div className="mb-4 p-4 rounded-lg border border-zinc-700 bg-zinc-800 text-sm space-y-2">

                        {accountInfo.userType === "Company" ? (
                            <>
                                <RevealField
                                    label="Company ID"
                                    value={accountInfo.companyId}
                                    show={showCompanyId}
                                    toggle={() => setShowCompanyId(prev => !prev)}
                                />

                                <RevealField
                                    label="User ID"
                                    value={accountInfo.userId}
                                    show={showUserId}
                                    toggle={() => setShowUserId(prev => !prev)}
                                />

                                <p>User Type: {accountInfo.userType}</p>
                                <p className="text-xs text-zinc-400">
                                    Approve All: {accountInfo.approveAllLocations ? "Yes" : "No"} | 
                                    Restrict Subaccount: {accountInfo.restrictSubAccount ? "Yes" : "No"}
                                </p>
                            </>
                        ) : (
                            <>
                                <RevealField
                                    label="Location ID"
                                    value={accountInfo.locationId}
                                    show={showLocationId}
                                    toggle={() => setShowLocationId(prev => !prev)}
                                />

                                <RevealField
                                    label="Company ID"
                                    value={accountInfo.companyId}
                                    show={showCompanyId}
                                    toggle={() => setShowCompanyId(prev => !prev)}
                                />

                                <RevealField
                                    label="User ID"
                                    value={accountInfo.userId}
                                    show={showUserId}
                                    toggle={() => setShowUserId(prev => !prev)}
                                />

                                <p>User Type: {accountInfo.userType}</p>
                            </>
                        )}
                    </div>
                )}

                {/* Search Bar */}
                {accountInfo?.userType === "Company" && (
                    <div className="flex flex-col sm:flex-row gap-2 mb-3">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                placeholder="Search subaccounts..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pr-8 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200"
                            />

                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                                >
                                    <IoMdClose />
                                </button>
                            )}
                        </div>

                        <div className="flex gap-2 sm:justify-end">
                            <button
                                onClick={handleSelectAll}
                                className="flex-1 sm:flex-none px-3 py-2 text-xs bg-zinc-800 border border-zinc-600 rounded"
                            >
                                Select All
                            </button>

                            <button
                                onClick={handleDeselectAll}
                                className="flex-1 sm:flex-none px-3 py-2 text-xs bg-zinc-800 border border-zinc-600 rounded"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                )}

                {/* Subaccounts List */}
                {accountInfo?.userType === "Company" && (
                <div className="flex-grow overflow-y-auto space-y-3">
                    {!loading && filteredSubaccounts.map(sub => {
                        const isExpanded = expandedId === sub.id;
                        const isChecked = selectedSubResourceKeys.has(sub.id);

                        return (
                            <div key={sub.id} className="border border-zinc-700 rounded-lg">

                                <div
                                    className={`flex justify-between items-center p-3 cursor-pointer ${isChecked ? "bg-zinc-800" : "bg-zinc-900"}`}
                                    onClick={() => setExpandedId(prev => prev === sub.id ? null : sub.id)}
                                >
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleSubResource(sub.id);
                                            }}
                                            />

                                            <span
                                                className="cursor-pointer"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedId(prev => prev === sub.id ? null : sub.id);
                                                }}
                                            >
                                                {sub.name}
                                            </span>
                                        </div>

                                    <IoIosArrowDown className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                                </div>

                                {isExpanded && (
                                    <div className="bg-zinc-800 p-4 text-sm text-zinc-300 space-y-1">
                                        <p>Email: {sub.email || "N/A"}</p>
                                        <p>Phone: {sub.phone || "N/A"}</p>
                                        <p>City: {sub.city}, {sub.state}, {sub.country}</p>
                                        <p>Timezone: {sub.timezone}</p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                )}
                {loading && <p className="text-center text-zinc-400">Loading...</p>}

                {/* Footer */}
                <div className="mt-6 flex justify-end pt-4 border-t border-zinc-700">
                    <button
                        onClick={handleConfirmSelection}
                        disabled={confirmLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
                    >
                        {confirmLoading ? "Saving..." : "Save Configuration"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GohighlevelResourceModal;
